import type {
  ConnectionStatus,
  ConnectionsPayload,
  ConnectionsUsageAnalytics,
} from "@shared/types/connections";
import type { AiFailoverHistoryResponse, AiProvidersResponse } from "@shared/types/aiProviders";
import { apiClient } from "./apiClient";

const EMPTY_RESPONSE: ConnectionsPayload = {
  summary: {
    totalNewsProviders: 0,
    totalAiBackends: 0,
    totalInfrastructureServices: 0,
    hasAnyConnections: false,
    lastRefreshedAt: null,
    message: "No connections registered yet. Add providers to populate this view.",
  },
  newsProviders: {
    items: [],
    meta: {
      hasItems: false,
      emptyStateMessage: "No providers registered yet. Add one to start ingesting market news.",
      lastUpdatedAt: null,
    },
  },
  aiBackends: {
    items: [],
    meta: {
      hasItems: false,
      emptyStateMessage: "No AI backends connected. Provision a model to enable insight generation.",
      lastUpdatedAt: null,
    },
  },
  infrastructureServices: {
    items: [],
    meta: {
      hasItems: false,
      emptyStateMessage:
        "No infrastructure services documented yet. Register services to monitor them.",
      lastUpdatedAt: null,
    },
  },
  changeLog: [],
  warnings: ["Connections are not yet configured. Configure providers and services to enable automation."],
};

export async function fetchConnectionsData(): Promise<ConnectionsPayload> {
  try {
    const response = await apiClient.get<ConnectionsPayload>("/dashboard/connections");
    return response.data;
  } catch (error) {
    console.warn("[connectionsApi] Falling back to empty payload:", error);
    return EMPTY_RESPONSE;
  }
}

export type CreateNewsProviderInput = {
  name: string;
  category: string;
  status: ConnectionStatus;
  authenticationMode?: string;
  regions?: string[];
  dataTypes?: string[];
  notes?: string;
  secretId?: string | null;
};

export async function createNewsProvider(
  input: CreateNewsProviderInput,
): Promise<ConnectionsPayload> {
  const response = await apiClient.post<ConnectionsPayload>("/dashboard/connections/news-providers", input);
  return response.data;
}

export type UpdateNewsProviderInput = {
  name?: string;
  category?: string;
  status?: ConnectionStatus;
  authenticationMode?: string | null;
  regions?: string[];
  dataTypes?: string[];
  notes?: string | null;
  secretId?: string | null;
};

export async function updateNewsProvider(
  providerId: string,
  input: UpdateNewsProviderInput,
): Promise<ConnectionsPayload> {
  const response = await apiClient.patch<ConnectionsPayload>(
    `/dashboard/connections/news-providers/${encodeURIComponent(providerId)}`,
    input,
  );
  return response.data;
}

export async function deleteNewsProvider(providerId: string): Promise<ConnectionsPayload> {
  const response = await apiClient.delete<ConnectionsPayload>(
    `/dashboard/connections/news-providers/${encodeURIComponent(providerId)}`,
  );
  return response.data;
}

export type CreateAiBackendInput = {
  name: string;
  provider: string;
  model?: string;
  status: ConnectionStatus;
  failoverPriority?: number | null;
  notes?: string;
  secretId?: string | null;
  baseUrl?: string | null;
};

export async function createAiBackend(
  input: CreateAiBackendInput,
): Promise<ConnectionsPayload> {
  const response = await apiClient.post<ConnectionsPayload>("/dashboard/connections/ai-backends", input);
  return response.data;
}

export type UpdateAiBackendInput = {
  name?: string;
  provider?: string;
  model?: string | null;
  status?: ConnectionStatus;
  failoverPriority?: number | null;
  notes?: string | null;
  secretId?: string | null;
  baseUrl?: string | null;
};

export async function updateAiBackend(
  backendId: string,
  input: UpdateAiBackendInput,
): Promise<ConnectionsPayload> {
  const response = await apiClient.patch<ConnectionsPayload>(
    `/dashboard/connections/ai-backends/${encodeURIComponent(backendId)}`,
    input,
  );
  return response.data;
}

export async function deleteAiBackend(backendId: string): Promise<ConnectionsPayload> {
  const response = await apiClient.delete<ConnectionsPayload>(
    `/dashboard/connections/ai-backends/${encodeURIComponent(backendId)}`,
  );
  return response.data;
}

export type CreateInfrastructureServiceInput = {
  name: string;
  kind: string;
  endpoint?: string;
  status: ConnectionStatus;
  ownerTeam?: string;
  notes?: string;
  secretId?: string | null;
};

export async function createInfrastructureService(
  input: CreateInfrastructureServiceInput,
): Promise<ConnectionsPayload> {
  const response = await apiClient.post<ConnectionsPayload>(
    "/dashboard/connections/infrastructure-services",
    input,
  );
  return response.data;
}

export type UpdateInfrastructureServiceInput = {
  name?: string;
  kind?: string;
  endpoint?: string | null;
  status?: ConnectionStatus;
  ownerTeam?: string | null;
  notes?: string | null;
  secretId?: string | null;
};

export async function updateInfrastructureService(
  serviceId: string,
  input: UpdateInfrastructureServiceInput,
): Promise<ConnectionsPayload> {
  const response = await apiClient.patch<ConnectionsPayload>(
    `/dashboard/connections/infrastructure-services/${encodeURIComponent(serviceId)}`,
    input,
  );
  return response.data;
}

export async function deleteInfrastructureService(serviceId: string): Promise<ConnectionsPayload> {
  const response = await apiClient.delete<ConnectionsPayload>(
    `/dashboard/connections/infrastructure-services/${encodeURIComponent(serviceId)}`,
  );
  return response.data;
}

const EMPTY_AI_PROVIDERS: AiProvidersResponse = {
  providers: [],
  activeProviderId: null,
  refreshedAt: new Date(0).toISOString(),
};

const EMPTY_FAILOVER_HISTORY: AiFailoverHistoryResponse = {
  entries: [],
  count: 0,
  refreshedAt: new Date(0).toISOString(),
};

const EMPTY_USAGE_ANALYTICS: ConnectionsUsageAnalytics = {
  newsProviders: {
    total: 0,
    statusBreakdown: { online: 0, offline: 0, degraded: 0, unknown: 0 },
    lastUpdatedAt: null,
  },
  aiBackends: {
    total: 0,
    statusBreakdown: { online: 0, offline: 0, degraded: 0, unknown: 0 },
    lastUpdatedAt: null,
    active: 0,
  },
  infrastructureServices: {
    total: 0,
    statusBreakdown: { online: 0, offline: 0, degraded: 0, unknown: 0 },
    lastUpdatedAt: null,
  },
  changeLog: {
    totalEntries: 0,
    lastEntryAt: null,
    last24h: 0,
    last7d: 0,
    topActions: [],
  },
  failover: {
    totalSwitches: 0,
    lastSwitchAt: null,
    lastSwitchToId: null,
    lastSwitchToName: null,
    lastInitiatedBy: null,
    lastReason: null,
    lastNotes: null,
  },
};

export async function fetchAiProviders(): Promise<AiProvidersResponse> {
  try {
    const response = await apiClient.get<AiProvidersResponse>("/dashboard/connections/ai-providers");
    return response.data;
  } catch (error) {
    console.warn("[connectionsApi] Failed to load AI providers, using empty list.", error);
    return EMPTY_AI_PROVIDERS;
  }
}

export async function fetchAiFailoverHistory(limit = 25): Promise<AiFailoverHistoryResponse> {
  try {
    const response = await apiClient.get<AiFailoverHistoryResponse>(
      `/dashboard/connections/ai-providers/failovers?limit=${encodeURIComponent(limit)}`,
    );
    return response.data;
  } catch (error) {
    console.warn("[connectionsApi] Failed to load AI failover history, using empty list.", error);
    return EMPTY_FAILOVER_HISTORY;
  }
}

export async function fetchUsageAnalytics(): Promise<ConnectionsUsageAnalytics> {
  try {
    const response = await apiClient.get<ConnectionsUsageAnalytics>("/dashboard/connections/usage-analytics");
    return response.data;
  } catch (error) {
    console.warn("[connectionsApi] Failed to load usage analytics, using defaults.", error);
    return EMPTY_USAGE_ANALYTICS;
  }
}

export interface ActivateAiProviderInput {
  providerId: string;
  initiatedBy: string;
  reason: string;
  notes?: string;
}

export async function activateAiProvider(input: ActivateAiProviderInput): Promise<void> {
  await apiClient.post(
    `/dashboard/connections/ai-providers/${encodeURIComponent(input.providerId)}/activate`,
    {
      initiatedBy: input.initiatedBy,
      reason: input.reason,
      notes: input.notes,
    },
  );
}
